import React, { useEffect, useState } from "react";
import moment from "moment";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import Select from "react-select";
import {
  LuAlertCircle,
  LuCalendar,
  LuCreditCard,
  LuFileText,
  LuIndianRupee,
  LuListFilter,
} from "react-icons/lu";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import Button from "../ui/Button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/Dialog";
import Input from "../ui/Input";
import cx from "../ui/cx";
import {
  buttonSpinnerClass,
  dialogInputClass,
  dialogMobileBodyClass,
  dialogMobileContentClass,
  dialogMobileFooterClass,
  dialogMobileFormClass,
  dialogMobileHeaderClass,
  dialogTitleClass,
  fieldErrorIconClass,
  fieldErrorTextClass,
  fieldLabelClass,
  formFieldIconClass,
  formSelectIconClass,
} from "../ui/styles";
import {
  getAccountTypes,
  getEditAccount,
  getIsAccountModalOpen,
  setEditAccount,
  toggleAccountModal,
} from "../../features/accounts/accountsSlice";
import { iconSelectStyles } from "../../static/styles";
import { IFormInput, OptionsProps } from "../../types/accounts";

type Props = {
  handleSaveAccount: (data: IFormInput) => void;
};

const AccountFormModal = ({ handleSaveAccount }: Props) => {
  const dispatch = useAppDispatch();
  const editAccount = useAppSelector(getEditAccount);
  const isAccountModalOpen = useAppSelector(getIsAccountModalOpen);
  const accountTypes = useAppSelector(getAccountTypes);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [accountTypeOption, setAccountTypeOption] = useState<OptionsProps[]>(
    []
  );
  const [statusOption] = useState<OptionsProps[]>([
    { value: 0, label: "આવક" },
    { value: 1, label: "જાવક" },
  ]);

  const {
    handleSubmit,
    register,
    reset,
    control,
    setValue,
    formState: { errors },
  } = useForm<IFormInput>({
    defaultValues: {
      account_type_id: 0,
      amount: null,
      status: isAccountModalOpen === 1 ? 0 : 1,
      description: "",
      date: moment().format("YYYY-MM-DDTHH:mm"),
    },
  });

  const handleOnSubmit: SubmitHandler<IFormInput> = async (data: any) => {
    setIsLoading(true);
    await handleSaveAccount({
      ...data,
      date: moment(data.date).format("YYYY-MM-DD HH:mm:ss"),
    });
    handleCloseFormModal();
    setIsLoading(false);
  };

  const handleCloseFormModal = () => {
    reset();
    dispatch(toggleAccountModal(0));
    dispatch(setEditAccount(null));
  };

  useEffect(() => {
    const options =
      accountTypes?.map((accountType) => ({
        value: accountType.id,
        label: accountType.name,
      })) ?? [];
    setAccountTypeOption(options);
  }, [accountTypes]);

  useEffect(() => {
    if (editAccount && editAccount.id) {
      reset({
        account_type_id: editAccount.account_type_id,
        amount: editAccount.amount,
        status: editAccount.status,
        description: editAccount.description || "",
        date: moment(editAccount.date || editAccount.created_at).format(
          "YYYY-MM-DDTHH:mm"
        ),
      });
    } else {
      reset({
        account_type_id: 0,
        amount: null,
        status: isAccountModalOpen === 1 ? 0 : 1,
        description: "",
        date: moment().format("YYYY-MM-DDTHH:mm"),
      });
    }
  }, [editAccount, isAccountModalOpen, reset]);

  return (
    <Dialog open onClose={handleCloseFormModal}>
      <DialogContent
        size="md"
        className={cx("max-h-[90vh] overflow-y-auto", dialogMobileContentClass)}
      >
        <DialogHeader className={dialogMobileHeaderClass}>
          <DialogTitle className={dialogTitleClass}>
            {isAccountModalOpen === 1 ? "આવક" : "જાવક"} ની માહિતી
          </DialogTitle>
        </DialogHeader>

        <form
          onSubmit={handleSubmit(handleOnSubmit)}
          className={cx("space-y-4", dialogMobileFormClass)}
        >
          <div className={cx("space-y-4", dialogMobileBodyClass)}>
            <div className="space-y-2">
              <label className={fieldLabelClass}>
                તારીખ અને સમય <span className="text-red-500">*</span>
              </label>

              <div className="relative">
                <LuCalendar className={formSelectIconClass} />
                <Input
                  type="datetime-local"
                  className={dialogInputClass(!!errors.date)}
                  {...register("date", { required: true })}
                />
                {errors.date && (
                  <LuAlertCircle className={fieldErrorIconClass} />
                )}
              </div>

              {errors.date && (
                <p className={fieldErrorTextClass}>તારીખ અને સમય દાખલ કરો</p>
              )}
            </div>

            <div className="space-y-2">
              <label className={fieldLabelClass}>
                એકાઉન્ટ ટાઈપ <span className="text-red-500">*</span>
              </label>

              <div className="relative">
                <LuCreditCard className={formSelectIconClass} />
                <Controller
                  {...register("account_type_id", { required: true, min: 1 })}
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={
                        accountTypeOption.find(
                          (val) => val.value === field.value
                        ) || null
                      }
                      options={accountTypeOption}
                      placeholder="એકાઉન્ટ ટાઈપ સિલેક્ટ કરો"
                      styles={iconSelectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      menuShouldBlockScroll
                      classNames={{ menuPortal: () => "font-inter" }}
                      className={cx(
                        "react-select-container",
                        errors.account_type_id && "error"
                      )}
                      classNamePrefix="react-select"
                      onChange={(val: any) =>
                        setValue("account_type_id", val?.value)
                      }
                    />
                  )}
                />
              </div>

              {errors.account_type_id && (
                <p className={fieldErrorTextClass}>
                  એકાઉન્ટ ટાઈપ સિલેક્ટ કરો
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className={fieldLabelClass}>
                સ્ટેટ્સ <span className="text-red-500">*</span>
              </label>

              <div className="relative">
                <LuListFilter className={formSelectIconClass} />
                <Controller
                  {...register("status", { required: true })}
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={
                        statusOption.find(
                          (val) => val.value === field.value
                        ) || null
                      }
                      options={statusOption}
                      placeholder="સ્ટેટ્સ સિલેક્ટ કરો"
                      styles={iconSelectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      menuShouldBlockScroll
                      classNames={{ menuPortal: () => "font-inter" }}
                      className={cx(
                        "react-select-container",
                        errors.status && "error"
                      )}
                      classNamePrefix="react-select"
                      onChange={(val: any) => setValue("status", val?.value)}
                    />
                  )}
                />
              </div>

              {errors.status && (
                <p className={fieldErrorTextClass}>સ્ટેટ્સ સિલેક્ટ કરો</p>
              )}
            </div>

            <div className="space-y-2">
              <label className={fieldLabelClass}>
                અમાઉન્ટ <span className="text-red-500">*</span>
              </label>

              <div className="relative">
                <LuIndianRupee className={formFieldIconClass} />
                <Input
                  type="number"
                  step="any"
                  placeholder="અમાઉન્ટ દાખલ કરો"
                  className={dialogInputClass(!!errors.amount)}
                  {...register("amount", { required: true, min: 0 })}
                />
                {errors.amount && (
                  <LuAlertCircle className={fieldErrorIconClass} />
                )}
              </div>

              {errors.amount && (
                <p className={fieldErrorTextClass}>અમાઉન્ટ દાખલ કરો</p>
              )}
            </div>

            <div className="space-y-2">
              <label className={fieldLabelClass}>ડિસ્ક્રિપ્શન</label>

              <div className="relative">
                <LuFileText className={formFieldIconClass} />
                <Input
                  placeholder="ડિસ્ક્રિપ્શન દાખલ કરો"
                  className={dialogInputClass()}
                  {...register("description")}
                />
              </div>
            </div>
          </div>

          <DialogFooter className={dialogMobileFooterClass}>
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={handleCloseFormModal}
              disabled={isLoading}
            >
              બંધ કરો
            </Button>

            <Button
              type="submit"
              variant="primary"
              className="flex-1"
              disabled={isLoading}
            >
              {isLoading && <span className={buttonSpinnerClass} />}
              આગળ વધો
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AccountFormModal;
